// MapPreviewEngine.js - OL lifecycle for inline read-only map embed previews.
import OLMap from 'ol/Map';
import View from 'ol/View';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import GeoJSON from 'ol/format/GeoJSON';
import {fromLonLat, transformExtent} from 'ol/proj';
import {apply as applyMapboxStyle} from 'ol-mapbox-style';
import {DEFAULT_BASEMAP, OVERLAY_TYPES} from './schema/mapSchema';
import {addCoordGridLayers, removeCoordGridLayers, updateCoordGrid} from './editor/coordGrid';
import {MIN_ZOOM} from './mapViewConstants';
// Ticket 02: report-embed satellite overlay reuses the same GIBS coverage/date logic
// MapEditorModal.jsx's `applyImageryCoverage` already relies on — imported directly rather than
// re-implemented (see `_applySatelliteOverlay` below).
import gibsImageryProvider from './editor/imagery/gibsImageryProvider';
// Marking-unification: the main markings layer (unit-icon SVG styling + point/line/polygon
// fallback) and the tactical-graphic sub-feature layer now both style via the SAME shared
// implementation the live editor (useMapEditor.js) uses — see renderer/markingStyle.js. This
// replaces this file's own private symbolCache/symbolStyle/featureStyle duplicates, and is the
// fix for tactical graphics (arrowheads, echelon ticks, dashes, etc.) rendering as a plain
// fallback dot/line/blob in report-embed previews: `_applyTacticalGeojson` below is the preview
// path's equivalent of useMapEditor's `syncTacticalSource`, populating a dedicated layer with
// the already-expanded tactical GeoJSON (built via `expandTacticalMarkingsToFeatures` — see
// MapPreviewQueueService.js's `_runJob`, which has the raw markings/DTG-cursor this expansion
// needs) instead of leaving tactical markings to fall through to the generic geometry fallback.
import {markingFeatureStyle, tacticalFeatureStyle} from './editor/renderer/markingStyle';
// Marking-unification (preview parity): CBRN-hatch/minefield-perimeter-stamp canvas overlay —
// same class the live editor attaches to its LYR_TACTICAL layer (see useMapEditor.js's
// setupOlLayers). It's engine-agnostic (just `(layer, map)` + a `postrender` hook reading
// style/geometry generically), so it works unmodified against `_tacticalLayer`/`_map` here too —
// see `_createMap`/`destroy` below.
import {AreaFillOverlay} from './editor/renderer/AreaFillOverlay';

const FORMAT = new GeoJSON({featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326'});

function bboxKey(bbox) {
    if (!bbox) return '';
    return bbox[0][0] + ',' + bbox[0][1] + ',' + bbox[1][0] + ',' + bbox[1][1];
}

// Ticket 02: `MapPreviewEngine`'s `bbox` param is the nested `[[swLng,swLat],[neLng,neLat]]`
// shape (see `bboxKey` above / `mapPmAdapter.js`'s `resolveViewBbox`), but `ImageryProvider`'s
// `getCoverage` contract (see `editor/imagery/ImageryProvider.js`) wants the flat
// `[minLon, minLat, maxLon, maxLat]` shape MapEditorModal's `viewBboxLonLat` produces. Reshape
// only — no coverage/date logic lives here.
function bboxToFlatLonLat(bbox) {
    if (!bbox) return null;
    const [[minLon, minLat], [maxLon, maxLat]] = bbox;
    return [minLon, minLat, maxLon, maxLat];
}

export class MapPreviewEngine {
    constructor(container) {
        this._container      = container;
        this._map            = null;
        this._dataLayer      = null;
        // Marking-unification: dedicated tactical-graphic layer, mirroring the live editor's
        // LYR_TACTICAL — see `_createMap`/`_applyTacticalGeojson` below.
        this._tacticalLayer  = null;
        // Marking-unification (preview parity): CBRN-hatch/minefield-perimeter-stamp overlay for
        // `_tacticalLayer` — see `_createMap`/`destroy` below.
        this._areaFillOverlay = null;
        this._destroyed      = false;
        this._paused         = false;
        this._currentBasemap = null;
        this._lastBboxKey    = null;
        this._pendingSync    = null;
        // Ticket 02: lazily-created GIBS raster TileLayer for the 'satellite' overlay — mirrors
        // MapEditorModal's `baseTileLayerRef` (see `applyImageryCoverage`), one per engine
        // instance rather than per-node since this engine is itself shared across all map-node
        // previews in a report (see MapPreviewQueueService).
        this._satelliteLayer = null;
    }

    async sync({
        basemap, geojson, tacticalGeojson = null, bbox, fitBounds = false, fitPadding = 0, gridType = null,
        overlayType = OVERLAY_TYPES.MAPBOX, currentDtgMs = null,
    }) {
        if (this._destroyed || this._paused) {
            this._pendingSync = {basemap, geojson, tacticalGeojson, bbox, fitBounds, fitPadding, gridType, overlayType, currentDtgMs};
            return;
        }
        const styleId = basemap || DEFAULT_BASEMAP;
        if (!this._map) {
            // ponytail (debug fa0f75, H1 fix): await the basemap style application before this
            // sync() call returns. Previously this was fire-and-forget, so captureSnapshot()
            // (called right after sync() by MapPreviewQueueService) could renderSync()+capture
            // before the async style fetch ever added the basemap tile layers — producing a
            // snapshot with only the data/symbols layer and no map background underneath it.
            await this._createMap(styleId);
        } else if (this._currentBasemap !== styleId) {
            const toRemove = this._map.getLayers().getArray().filter((l) => l.get('mapbox-layers'));
            toRemove.forEach((l) => this._map.removeLayer(l));
            this._currentBasemap = styleId;
            await applyMapboxStyle(this._map, styleId)
                .catch((err) => {
                    console.warn('[MapPreviewEngine] basemap swap failed', err);
                });
        }
        if (this._destroyed || !this._map) return;
        this._applyGeojson(geojson);
        this._applyTacticalGeojson(tacticalGeojson);
        this._applyGrid(gridType);
        this._applySatelliteOverlay(overlayType, bbox, currentDtgMs);
        const nextKey = bboxKey(bbox);
        if (fitBounds && bbox && nextKey !== this._lastBboxKey) {
            this._lastBboxKey = nextKey;
            const w = bbox[0][0]; const s = bbox[0][1]; const e = bbox[1][0]; const n = bbox[1][1];
            const pad = [fitPadding, fitPadding, fitPadding, fitPadding];
            this._map.getView().fit(transformExtent([w, s, e, n], 'EPSG:4326', 'EPSG:3857'), {padding: pad, duration: 0});
        }
        this._map.updateSize();
    }

    pause() { this._paused = true; }

    resume() {
        this._paused = false;
        if (this._map) this._map.updateSize();
        if (this._pendingSync) {
            const pending = this._pendingSync;
            this._pendingSync = null;
            void this.sync(pending);
        }
    }

    resize() { if (this._map) this._map.updateSize(); }

    captureSnapshot() {
        return new Promise((resolve) => {
            if (!this._map) return resolve('');
            this._map.once('rendercomplete', () => {
                try {
                    const canvas = this._map.getViewport().querySelector('canvas');
                    // WebP-preview-size ticket: a quality of 1.0 requests LOSSLESS WebP encoding
                    // (Chromium/Firefox 105+ treat 1.0 as "lossless", not merely "max lossy
                    // quality" — same convention as toDataURL('image/jpeg', 1.0) meaning max
                    // fidelity), typically 20-30%+ smaller than PNG for this flat-color/line/
                    // label tactical-map content with zero fidelity loss. Browsers without WebP
                    // toDataURL support (e.g. Safari, per the Canvas spec) silently fall back to
                    // image/png — no explicit feature-detection branch needed; downstream
                    // consumers (img src, docx export, sitrep vision payload) already accept
                    // either format via the generic data: URL prefix.
                    const dataUrl = canvas ? canvas.toDataURL('image/webp', 1.0) : '';
                    resolve(dataUrl);
                } catch (err) {
                    console.warn('[MapPreviewEngine] captureSnapshot failed', err);
                    resolve('');
                }
            });
            this._map.renderSync();
        });
    }

    destroy() {
        this._destroyed  = true;
        this._pendingSync = null;
        if (this._areaFillOverlay) { this._areaFillOverlay.destroy(); this._areaFillOverlay = null; }
        if (this._map) { this._map.setTarget(undefined); this._map = null; }
        this._dataLayer  = null;
        this._tacticalLayer = null;
        this._satelliteLayer = null;
        this._currentBasemap = null;
        this._lastBboxKey    = null;
    }

    async _createMap(styleId) {
        this._container.replaceChildren();
        this._dataLayer = new VectorLayer({source: new VectorSource(), style: (f) => markingFeatureStyle(f), zIndex: 200});
        // Ticket (marking-unification): dedicated tactical-graphic layer, mirroring the live
        // editor's LYR_TACTICAL — drawn ABOVE the main markings layer (zIndex 201 > 200) so the
        // fully-expanded tactical render (arrowheads/echelons/dashes) covers the plain fallback
        // geometry `_dataLayer` also draws for the same tactical marking (same double-render-but-
        // covered approach useMapEditor.js's LYR_FILLS/LYR_TACTICAL pair already relies on).
        this._tacticalLayer = new VectorLayer({source: new VectorSource(), style: (f) => tacticalFeatureStyle(f), zIndex: 201});
        this._map = new OLMap({
            target: this._container,
            layers: [this._dataLayer, this._tacticalLayer],
            view: new View({center: fromLonLat([0, 20]), zoom: 2, minZoom: MIN_ZOOM}),
            interactions: [],
            controls: [],
        });
        // Marking-unification (preview parity): CBRN-hatch/minefield-perimeter-stamp fills for
        // tactical-area markings — mirrors useMapEditor.js's setupOlLayers, which attaches the
        // same overlay to its own tactical layer right after creating it. `AreaFillOverlay` reads
        // style/geometry off whatever `(layer, map)` pair it's given, so no changes were needed
        // inside AreaFillOverlay.js itself to reuse it here.
        this._areaFillOverlay = new AreaFillOverlay(this._tacticalLayer, this._map);
        this._currentBasemap = styleId;
        this._lastBboxKey    = null;
        // A fresh map has no satellite TileLayer yet — any layer from a prior map instance is
        // gone along with it (setTarget(undefined) in destroy(), or this is the very first map).
        this._satelliteLayer = null;
        // ponytail (debug fa0f75, H1 fix): applyMapboxStyle adds the basemap tile layers at
        // z-index < dataLayer, but does so asynchronously (fetches + parses the style JSON).
        // This is now awaited by the caller (sync()) so captureSnapshot() never runs against a
        // map that's missing its basemap layers — see sync() for details.
        await applyMapboxStyle(this._map, styleId)
            .catch((err) => {
                console.warn('[MapPreviewEngine] initial basemap apply failed', err);
            });
    }

    _applyGrid(gridType) {
        const map = this._map;
        if (!map) return;
        if (!gridType) { removeCoordGridLayers(map); return; }
        addCoordGridLayers(map);
        updateCoordGrid(map, gridType);
    }

    // Ticket 02: report-embed mirror of MapEditorModal.jsx's `applyImageryCoverage` — same
    // GIBS coverage-checked raster TileLayer, same zIndex convention (1: above the vector
    // basemap's unspecified/effectively-0 zIndex, below `_dataLayer`'s 200), same "only ever
    // hide/show one layer, never touch the vector basemap underneath" strategy. Differences from
    // the editor version are only about *inputs*: no live DTG cursor or React refs here — just
    // the map doc's persisted `overlayType`/`currentDtgMs` (threaded in via `sync()`) and the
    // `bbox` param `sync()` already receives for view-fitting.
    //
    // Coverage/date logic itself is NOT reimplemented — `gibsImageryProvider.getCoverage()` is
    // called directly, same as the editor does.
    _applySatelliteOverlay(overlayType, bbox, currentDtgMs) {
        const map = this._map;
        if (!map) return;

        if (overlayType !== OVERLAY_TYPES.SATELLITE) {
            if (this._satelliteLayer) this._satelliteLayer.setVisible(false);
            return;
        }

        const flatBbox = bboxToFlatLonLat(bbox);
        const coverage = gibsImageryProvider.getCoverage(flatBbox, currentDtgMs);

        if (coverage.hasCoverage && coverage.tileUrlTemplate) {
            let layer = this._satelliteLayer;
            if (!layer) {
                layer = new TileLayer({
                    source: new XYZ({url: coverage.tileUrlTemplate, crossOrigin: 'anonymous'}),
                    zIndex: 1,
                });
                layer.set('id', '__gibs-imagery__');
                this._satelliteLayer = layer;
                map.addLayer(layer);
            } else if (layer.getSource()?.getUrls?.()[0] !== coverage.tileUrlTemplate) {
                layer.setSource(new XYZ({url: coverage.tileUrlTemplate, crossOrigin: 'anonymous'}));
            }
            layer.setVisible(true);
        } else if (this._satelliteLayer) {
            this._satelliteLayer.setVisible(false);
        }
    }

    _applyGeojson(geojson) {
        this._loadFeaturesInto(this._dataLayer, geojson);
    }

    // Marking-unification: preview-path equivalent of useMapEditor.js's `syncTacticalSource` —
    // populates `_tacticalLayer` from the already-expanded tactical GeoJSON built by
    // `expandTacticalMarkingsToFeatures` (renderer/markingStyle.js), which MapPreviewQueueService
    // computes before calling `sync()` (it has the raw markings + DTG cursor this expansion step
    // needs; this engine only ever sees resolved GeoJSON). Each expanded sub-feature already
    // carries the `_props` shape `tacticalFeatureStyle` expects (tacticalSidc/markingId/
    // isCurrent).
    _applyTacticalGeojson(tacticalGeojson) {
        this._loadFeaturesInto(this._tacticalLayer, tacticalGeojson);
    }

    // Shared read-GeoJSON-into-a-VectorLayer's-source step for both `_dataLayer` and
    // `_tacticalLayer` — same shape, only the target layer differs (see `_applyGeojson`/
    // `_applyTacticalGeojson` above).
    _loadFeaturesInto(layer, geojson) {
        if (!layer) return;
        const source = layer.getSource();
        source.clear(true);
        if (!geojson || !geojson.features || !geojson.features.length) return;
        const features = geojson.features.map(function(f) {
            const olF = FORMAT.readFeature(f);
            if (f.id != null) olF.setId(f.id);
            olF.set('_props', f.properties || {});
            return olF;
        });
        source.addFeatures(features);
    }
}
